import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../context/ThemeContext";
import { getAllThemes } from "../../themes/themeConfig";
import {
  Palette,
  Check,
  Plus,
  X,
  RotateCcw,
  Type,
  Sun,
  Moon,
  Minus
} from "lucide-react";

const COLOR_FIELDS = [
  { key: "primary", label: "Primary Color" },
  { key: "secondary", label: "Accent Color" },
  { key: "background", label: "App Background" },
  { key: "formBg", label: "Card / Form Background" },
  { key: "headerBg", label: "Header Bar Background" },
  { key: "sidebar", label: "Sidebar Base" },
  { key: "sidebarHover", label: "Sidebar Hover" },
  { key: "sidebarActive", label: "Sidebar Active" },
  { key: "text", label: "Primary Text" },
  { key: "textLight", label: "Muted Text" },
  { key: "border", label: "UI Borders" },
  { key: "inputBorder", label: "Input Borders" },
  { key: "success", label: "Success Color" },
  { key: "error", label: "Error Color" },
  { key: "warning", label: "Warning Color" },
];

const FONTS = [
  { label: "Inter", value: "Inter" },
  { label: "Poppins", value: "Poppins" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Outfit", value: "Outfit" },
  { label: "Plus Jakarta", value: "Plus Jakarta Sans" },
  { label: "Lexend", value: "Lexend" },
  { label: "Nunito", value: "Nunito" },
  { label: "Roboto", value: "Roboto" },
  { label: "Kanit", value: "Kanit" },
];

const getCustomThemeFromStorage = () => {
  try {
    const data = localStorage.getItem("customTheme");
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const ThemeSelector = ({ onItemClick }) => {
  const {
    currentTheme,
    themeColors,
    themeMode,
    setThemeMode,
    changeTheme,
    setCustomTheme,
    currentFont,
    changeFont,
    currentFontSize,
    changeFontSize,
    resetSettings,
  } = useTheme();

  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customColors, setCustomColors] = useState(() => {
    const saved = getCustomThemeFromStorage();
    return (
      saved?.colors || {
        primary: "#FF7A1A",
        secondary: "#FF9340",
        background: "#0B0B0C",
        formBg: "#141416",
        headerBg: "#101011",
        sidebar: "#101011",
        sidebarHover: "#19191c",
        sidebarActive: "#FF7A1A",
        text: "#F7F7F7",
        textLight: "#BDBDBD",
        border: "rgba(255, 255, 255, 0.08)",
        inputBorder: "#FF7A1A",
        success: "#22C55E",
        error: "#EF4444",
        warning: "#EAB308",
      }
    );
  });

  const [customTheme, setLocalCustomTheme] = useState(() => getCustomThemeFromStorage());

  const handleThemeChange = (themeKey) => {
    changeTheme(themeKey);
    if (onItemClick) onItemClick();
  };

  const handleCustomOpen = (e) => {
    e.stopPropagation();
    setCustomDialogOpen(true);
  };

  const handleCustomClose = () => {
    setCustomDialogOpen(false);
  };

  const handleColorChange = (key, value) => {
    setCustomColors((prev) => ({ ...prev, [key]: value }));
  };

  const handleCustomSave = () => {
    const theme = {
      key: "custom",
      name: "Custom Theme",
      colors: {
        ...customColors,
        white: customColors.formBg || "#141416",
        shadow: "rgba(0,0,0,0.25)",
        info: "#3B82F6",
      },
    };
    localStorage.setItem("customTheme", JSON.stringify(theme));
    setCustomTheme(theme);
    setLocalCustomTheme(theme);
    changeTheme("custom");
    setCustomDialogOpen(false);
    if (onItemClick) onItemClick();
  };

  const handleIncreaseFont = () => {
    const currentSize = parseInt(currentFontSize.replace("px", "")) || 14;
    if (currentSize < 24) changeFontSize(`${currentSize + 1}px`);
  };

  const handleDecreaseFont = () => {
    const currentSize = parseInt(currentFontSize.replace("px", "")) || 14;
    if (currentSize > 10) changeFontSize(`${currentSize - 1}px`);
  };

  let themes = getAllThemes();
  if (customTheme) {
    themes = [customTheme, ...themes.filter((t) => t.key !== "custom")];
  }

  return (
    <div style={{ width: "100%", color: themeColors.text, fontFamily: "var(--app-font-family)" }}>
      {/* Header Section */}
      <div style={{
        padding: "14px 16px",
        background: `linear-gradient(135deg, ${themeColors.primary} 0%, rgba(var(--app-primary-color-rgb), 0.8) 100%)`,
        color: "#fff",
        borderRadius: "12px 12px 0 0",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Palette size={16} />
            <span style={{ fontWeight: 800, fontSize: '0.9em', letterSpacing: "0.5px", textTransform: "uppercase" }}>NT Monitor Theme Engine</span>
          </div>
        </div>
      </div>

      <div style={{ padding: "14px", background: themeColors.formBg, borderRadius: "0 0 12px 12px" }}>
        {/* Custom and Reset Buttons */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <button
            onClick={handleCustomOpen}
            style={{
              flex: 1,
              padding: "8px",
              background: "rgba(var(--app-primary-color-rgb), 0.08)",
              border: "1px dashed var(--app-primary-color)",
              borderRadius: "10px",
              color: "var(--app-primary-color)",
              fontWeight: 800,
              fontSize: '0.7em',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            <Plus size={14} /> CUSTOM THEME
          </button>

          <button
            onClick={resetSettings}
            style={{
              flex: 1,
              padding: "8px",
              background: themeMode === 'dark' ? "rgba(255,255,255,0.05)" : "#fff",
              border: "1px solid #fee2e2",
              borderRadius: "10px",
              color: "#ef4444",
              fontWeight: 800,
              fontSize: '0.7em',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            <RotateCcw size={14} /> RESET
          </button>
        </div>

        {/* Light / Dark Mode Toggle */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <button
            onClick={() => { setThemeMode('light'); if (onItemClick) onItemClick(); }}
            style={{
              flex: 1,
              padding: "10px 8px",
              borderRadius: "10px",
              border: themeMode === 'light' ? "2px solid var(--app-primary-color)" : `1px solid ${themeColors.border}`,
              background: themeMode === 'light' ? "rgba(var(--app-primary-color-rgb), 0.12)" : themeColors.background,
              color: themeMode === 'light' ? "var(--app-primary-color)" : themeColors.textLight,
              fontWeight: 800,
              fontSize: '0.8em',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            <Sun size={16} /> LIGHT MODE
          </button>
          <button
            onClick={() => { setThemeMode('dark'); if (onItemClick) onItemClick(); }}
            style={{
              flex: 1,
              padding: "10px 8px",
              borderRadius: "10px",
              border: themeMode === 'dark' ? "2px solid var(--app-primary-color)" : `1px solid ${themeColors.border}`,
              background: themeMode === 'dark' ? "rgba(var(--app-primary-color-rgb), 0.12)" : themeColors.background,
              color: themeMode === 'dark' ? "var(--app-primary-color)" : themeColors.textLight,
              fontWeight: 800,
              fontSize: '0.8em',
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            <Moon size={16} /> DARK MODE
          </button>
        </div>

        {/* Themes Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px", marginBottom: "20px" }}>
          {themes.map((theme) => {
            const isActive = currentTheme === theme.key;
            return (
              <div
                key={theme.key}
                onClick={() => handleThemeChange(theme.key)}
                title={theme.name}
                style={{
                  position: "relative",
                  padding: "8px",
                  borderRadius: "12px",
                  cursor: "pointer",
                  background: isActive ? "rgba(var(--app-primary-color-rgb), 0.1)" : themeColors.background,
                  border: isActive ? `1.5px solid var(--app-primary-color)` : `1.5px solid ${themeColors.border}`,
                  transition: "all 0.2s",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  height: "72px",
                  boxSizing: "border-box"
                }}
              >
                <div style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: theme.colors.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: themeMode === 'dark' ? "2px solid #334155" : "2px solid rgba(255, 255, 255, 0.99)",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                }}>
                  <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: theme.colors.secondary || theme.colors.sidebarHover }} />
                </div>

                <span style={{
                  fontWeight: isActive ? 800 : 600,
                  fontSize: '0.65em',
                  textAlign: "center",
                  color: isActive ? "var(--app-primary-color)" : themeColors.textLight,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%"
                }}>{theme.name}</span>

                {isActive && (
                  <div style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    background: "var(--app-primary-color)",
                    color: "#fff",
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <Check size={8} strokeWidth={4} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Font & Typography Settings */}
        <div style={{
          background: "rgba(var(--app-primary-color-rgb), 0.04)",
          borderRadius: "12px",
          padding: "12px",
          border: `1px solid ${themeColors.border}`
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <div style={{ fontWeight: 800, fontSize: '0.7em', color: "var(--app-primary-color)", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: "6px" }}>
              <Type size={12} /> Typography Options
            </div>
            <button
              onClick={() => changeFont("Inter")}
              style={{
                padding: "4px 10px",
                background: "rgba(var(--app-primary-color-rgb), 0.1)",
                color: "var(--app-primary-color)",
                border: "1px solid rgba(var(--app-primary-color-rgb), 0.2)",
                borderRadius: "6px",
                fontSize: '0.65em',
                fontWeight: 800,
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "all 0.2s"
              }}
            >
              Default
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
            {FONTS.map(font => (
              <div
                key={font.value}
                onClick={() => changeFont(font.value)}
                style={{
                  padding: "5px 9px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: '0.7em',
                  fontFamily: font.value,
                  background: currentFont === font.value ? "var(--app-primary-color)" : themeColors.background,
                  color: currentFont === font.value ? "#fff" : themeColors.textLight,
                  fontWeight: 700,
                  border: `1px solid ${themeColors.border}`,
                  transition: "all 0.2s"
                }}
              >
                {font.value}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "4px", marginBottom: "10px" }}>
            {["12px", "14px", "16px", "18px", "20px"].map(sz => (
              <button
                key={sz}
                onClick={() => changeFontSize(sz)}
                style={{
                  flex: 1,
                  padding: "5px 0",
                  borderRadius: "6px",
                  border: `1px solid ${currentFontSize === sz ? "var(--app-primary-color)" : themeColors.border}`,
                  background: currentFontSize === sz ? "rgba(var(--app-primary-color-rgb), 0.18)" : themeColors.background,
                  color: currentFontSize === sz ? "var(--app-primary-color)" : themeColors.textLight,
                  fontWeight: currentFontSize === sz ? 800 : 600,
                  fontSize: "0.7em",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
              >
                {sz}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: themeColors.background, padding: "6px 10px", borderRadius: "10px", border: `1px solid ${themeColors.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button onClick={handleDecreaseFont} style={{ width: "28px", height: "28px", borderRadius: "6px", border: "none", background: "rgba(var(--app-primary-color-rgb), 0.1)", cursor: "pointer", color: "var(--app-primary-color)", display: "flex", alignItems: "center", justifyContent: "center" }}><Minus size={12} /></button>
              <div style={{ width: "40px", textAlign: "center" }}>
                <span style={{ fontWeight: 800, color: "var(--app-primary-color)", fontSize: '0.85em' }}>{currentFontSize}</span>
              </div>
              <button onClick={handleIncreaseFont} style={{ width: "28px", height: "28px", borderRadius: "6px", border: "none", background: "rgba(var(--app-primary-color-rgb), 0.1)", cursor: "pointer", color: "var(--app-primary-color)", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={12} /></button>
            </div>

            <button
              onClick={() => changeFontSize("14px")}
              style={{
                padding: "4px 10px",
                background: "rgba(var(--app-primary-color-rgb), 0.1)",
                color: "var(--app-primary-color)",
                border: "1px solid rgba(var(--app-primary-color-rgb), 0.2)",
                borderRadius: "6px",
                fontSize: '0.65em',
                fontWeight: 800,
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "all 0.2s"
              }}
            >
              Default
            </button>
          </div>
        </div>
      </div>

      {/* Studio Designer Modal (Custom Color Customization) */}
      {customDialogOpen && createPortal(
        <div
          className="studio-color-designer-portal"
          data-portal="theme-portal"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(8px)"
          }}
        >
          <div style={{
            width: "90%",
            maxWidth: "460px",
            background: themeColors.formBg,
            color: themeColors.text,
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)",
            border: `1px solid ${themeColors.border}`,
            animation: "fadeIn 0.3s ease-out"
          }}>
            <div style={{
              padding: "20px 24px",
              background: `linear-gradient(135deg, ${customColors.primary} 0%, ${customColors.sidebar} 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: '1.15em', textTransform: "uppercase", letterSpacing: "1px" }}>Studio Color Designer</div>
                <div style={{ fontSize: '0.7em', opacity: 0.9, fontWeight: 600 }}>DYNAMIC INTERFACE IDENTITY</div>
              </div>
              <button onClick={handleCustomClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={18} /></button>
            </div>

            <div style={{ padding: "20px", maxHeight: "400px", overflowY: "auto", background: themeColors.background }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {COLOR_FIELDS.map((field) => (
                  <div key={field.key} style={{
                    padding: "10px",
                    background: themeColors.formBg,
                    borderRadius: "12px",
                    border: `1px solid ${themeColors.border}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 800, fontSize: '0.65em', color: themeColors.textLight, textTransform: "uppercase" }}>{field.label}</span>
                      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: customColors[field.key] }} />
                    </div>

                    <div style={{ position: "relative" }}>
                      <div style={{
                        width: "100%", height: "32px", borderRadius: "8px",
                        background: customColors[field.key], border: `1px solid ${themeColors.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center"
                      }}>
                        <span style={{ color: "#fff", fontSize: '0.65em', fontWeight: 900, textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>{customColors[field.key].toUpperCase()}</span>
                        <input
                          type="color"
                          value={customColors[field.key]}
                          onChange={(e) => handleColorChange(field.key, e.target.value)}
                          style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: "16px 24px", background: themeColors.formBg, display: "flex", gap: "10px", borderTop: `1px solid ${themeColors.border}` }}>
              <button onClick={handleCustomClose} style={{ flex: 1, padding: "10px", borderRadius: "10px", border: `1.5px solid ${themeColors.border}`, background: themeColors.background, fontWeight: 800, fontSize: '0.8em', color: themeColors.textLight, cursor: "pointer" }}>CANCEL</button>
              <button onClick={handleCustomSave} style={{ flex: 1.5, padding: "10px", borderRadius: "10px", border: "none", background: customColors.primary, color: "#fff", fontWeight: 800, fontSize: '0.8em', cursor: "pointer" }}>SAVE IDENTITY</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ThemeSelector;
